import { z } from "zod";
import { LocalizedTextSchema, idPattern } from "./common.js";

export const BuildingCategorySchema = z.enum([
  "core",
  "production",
  "units",
  "research",
  "economy",
  "defense",
  "social",
]);
export type BuildingCategory = z.infer<typeof BuildingCategorySchema>;

/** An attribute either grows geometrically (`base × growth^(L−1)`) or is listed per level (index 0 = level 1). */
export const AttributeCurveSchema = z.union([
  z.object({ base: z.number(), growth: z.number().positive(), round: z.enum(["int", "none"]).optional() }),
  z.object({ perLevel: z.array(z.number()).length(30) }),
]);
export type AttributeCurve = z.infer<typeof AttributeCurveSchema>;

export const BuildingDefSchema = z
  .object({
    id: idPattern("bld"),
    name: LocalizedTextSchema,
    category: BuildingCategorySchema,
    footprint: z.union([z.object({ w: z.number().int().min(1).max(6), h: z.number().int().min(1).max(6) }), z.literal("ring")]),
    /** `gateFrom`: HQ level from which the type counts in the Querschnitt average (defaults to `first`, D65). */
    unlock: z.object({ first: z.number().int().min(1).max(30), copies: z.array(z.number().int().min(2).max(30)), gateFrom: z.number().int().min(1).max(30).optional() }),
    upgradeable: z.boolean(),
    timeWeight: z.number().min(0).max(1.5),
    costWeight: z.number().min(0).max(1.5),
    attributes: z.record(z.string(), AttributeCurveSchema),
    panel: z.string().min(1),
    description: LocalizedTextSchema,
  })
  .superRefine((b, ctx) => {
    let prev = b.unlock.first;
    for (const c of b.unlock.copies) {
      if (c <= prev) ctx.addIssue({ code: "custom", message: `copy unlock levels must increase after ${prev}`, path: ["unlock", "copies"] });
      prev = c;
    }
    if (!b.upgradeable && Object.keys(b.attributes).length > 0)
      ctx.addIssue({ code: "custom", message: "shortcut buildings have no attributes", path: ["attributes"] });
  });
export type BuildingDef = z.infer<typeof BuildingDefSchema>;

export interface BuildingLevel {
  level: number;
  cost: Record<"RAT" | "POL" | "ALY" | "ENC", number>;
  timeSec: number;
  power: number;
  attributes: Record<string, number>;
}
